// MUI Drawer + AppBar layout for /app/* (sub-plan 13 §13.4).
// Sidebar nav per spec, with permission-gated items via useAdminMe().
//
// Loading-safe minimal state (sub-plan 13 §13.4):
//   - During isLoading or on isError: render only Dashboard + Sign out.
//   - Treat admin_permissions === null (bootstrap edge case) the same as
//     "no permissions" via optional chaining.
//
// Sign out handler (R32/R33): queryClient.clear() BEFORE supabase.auth.signOut()
// + redirect to /login — prevents cross-account leak when admin B signs in
// on the same browser.

import { useMemo, useState, type ReactNode } from "react";
import {
  AppBar,
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Home as HomeIcon,
  PeopleAlt as AdminUsersIcon,
  Group as AppUsersIcon,
  Work as JobsIcon,
  Notifications as PushIcon,
  Assessment as ReportIcon,
  Settings as ConfigIcon,
  CardGiftcard as RewardsIcon,
  SupportAgent as TicketsIcon,
  ExpandLess,
  ExpandMore,
  Logout as SignOutIcon,
  AccountCircle as ProfileIcon,
  SettingsOutlined as GearIcon,
  PowerSettingsNew as PowerIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { tokens } from "@findgeeks/ui-tokens";
import { supabase } from "@/lib/supabase";
import { queryClient } from "@/lib/queryClient";
import { useAdminMe } from "@/hooks/useAdminMe";
import { useTabs } from "@/hooks/useTabs";
import { TabsBar } from "./TabsBar";
import { defaultLabelForPath, iconKeyForPath } from "@/lib/routeIcons";

// Reference TVP2 §9 image2: long labels like "Admin users management" and
// "APP users Management" must NOT wrap OR truncate with an ellipsis. The
// previous 250px was tight enough that the chevron arrow + icon push
// "Admin users management" into "Admin users managem..." territory.
// 280px gives comfortable room with no truncation.
const SIDEBAR_WIDTH = 280;
const HEADER_HEIGHT = 56;

interface NavItem {
  label: string;
  path?: string;
  icon: ReactNode;
  permission?:
    | "can_manage_admins"
    | "can_manage_app_users"
    | "can_manage_jobs"
    | "can_send_push"
    | "can_view_reports"
    | "can_manage_pricing"
    | "can_manage_withdrawals";
  children?: Array<{
    label: string;
    path: string;
    permission?: NavItem["permission"];
  }>;
}

export function SidebarLayout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading, isError } = useAdminMe();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const perms = data?.admin_permissions ?? null;

  const can = (
    flag: NavItem["permission"] | undefined,
  ): boolean => {
    if (!flag) return true;
    return Boolean(perms?.[flag]);
  };

  // During loading or error: show only Dashboard + Sign out.
  const minimalSidebar = isLoading || isError;

  const navItems: NavItem[] = useMemo(() => {
    if (minimalSidebar) {
      return [
        { label: "Dashboard", path: "/app/dashboard", icon: <HomeIcon /> },
      ];
    }
    return [
      { label: "Dashboard", path: "/app/dashboard", icon: <HomeIcon /> },
      {
        label: "Admin users management",
        icon: <AdminUsersIcon />,
        permission: "can_manage_admins",
        children: [
          {
            label: "Add/Edit/Delete Users",
            path: "/app/admin-users",
            permission: "can_manage_admins",
          },
          {
            label: "Roles & Permissions",
            path: "/app/admin-users/roles",
            permission: "can_manage_admins",
          },
        ],
      },
      {
        label: "APP users Management",
        icon: <AppUsersIcon />,
        permission: "can_manage_app_users",
        children: [
          {
            label: "Add/Edit/Delete APP Users",
            path: "/app/app-users",
            permission: "can_manage_app_users",
          },
          {
            label: "Approve account applications",
            path: "/app/app-users/approval",
            permission: "can_manage_app_users",
          },
        ],
      },
      {
        label: "Job Management",
        icon: <JobsIcon />,
        permission: "can_manage_jobs",
        children: [
          {
            label: "Add/Edit Job",
            path: "/app/jobs",
            permission: "can_manage_jobs",
          },
          {
            label: "Disputes",
            path: "/app/disputes",
            permission: "can_manage_jobs",
          },
        ],
      },
      {
        label: "Push notifications",
        icon: <PushIcon />,
        permission: "can_send_push",
        children: [
          {
            label: "Send push notifications",
            path: "/app/notifications/push",
            permission: "can_send_push",
          },
          {
            label: "Email notifications",
            path: "/app/notifications/email",
            permission: "can_send_push",
          },
        ],
      },
      {
        label: "Report",
        icon: <ReportIcon />,
        permission: "can_view_reports",
        children: [
          {
            label: "Commission report",
            path: "/app/reports/commission",
            permission: "can_view_reports",
          },
          {
            label: "Billing report",
            path: "/app/reports/billing",
            permission: "can_view_reports",
          },
          {
            label: "Withdrawals",
            path: "/app/reports/withdrawals",
            permission: "can_manage_withdrawals",
          },
        ],
      },
      {
        label: "Reward Points",
        path: "/app/rewards",
        icon: <RewardsIcon />,
        permission: "can_manage_app_users",
      },
      {
        label: "Support tickets",
        path: "/app/tickets",
        icon: <TicketsIcon />,
        permission: "can_manage_app_users",
      },
      {
        label: "Config",
        icon: <ConfigIcon />,
        children: [
          {
            label: "Job Categories",
            path: "/app/config/job-categories",
            permission: "can_manage_jobs",
          },
          {
            label: "Price Tiers",
            path: "/app/config/price-tiers",
            permission: "can_manage_pricing",
          },
          {
            label: "FAQ Templates",
            path: "/app/config/faq",
            permission: "can_manage_app_users",
          },
        ],
      },
      {
        label: "My Profile",
        path: "/app/profile",
        icon: <ProfileIcon />,
      },
    ];
  }, [minimalSidebar]);

  // Show error toast once if /me failed (defense-in-depth alongside the
  // minimal sidebar fallback).
  if (isError && !errorMsg) {
    setErrorMsg("Could not load permissions — please refresh.");
  }

  // Closeable multi-tab state (sub-plan 13 §13.4 TabsBar — was deferred to
  // Phase-2 in earlier R-passes, promoted to Phase-1 here so the admin
  // matches the TVP2 §9 reference UI exactly).
  const { tabs, openAndNavigate, close: closeTab } = useTabs();

  const handleSignOut = async () => {
    queryClient.clear();
    await supabase.auth.signOut();
    navigate("/login", { replace: true });
  };

  const toggleGroup = (label: string) => {
    setOpenGroups((g) => ({ ...g, [label]: !g[label] }));
  };

  // Navigation wrapper for sidebar clicks: opens a tab AND navigates.
  // The tab label is always sourced from defaultLabelForPath() — keeps
  // tab labels canonical regardless of how the sidebar's own ListItemText
  // chooses to display the same item.
  const goToPage = (path: string, _sidebarLabel: string) => {
    openAndNavigate(path, defaultLabelForPath(path), iconKeyForPath(path));
  };

  const isActive = (path?: string) =>
    Boolean(path) && location.pathname === path;

  const isGroupActive = (item: NavItem) =>
    Boolean(item.children?.some((c) => isActive(c.path)));

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Top header bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          height: HEADER_HEIGHT,
          background: tokens.colors.admin.header,
        }}
      >
        <Toolbar
          variant="dense"
          sx={{ minHeight: HEADER_HEIGHT, height: HEADER_HEIGHT, gap: 1, px: 2 }}
        >
          {/* Brand "Find Geeks" — left side of teal AppBar, white text.
              Pinned to fixed width matching SIDEBAR_WIDTH so it visually
              owns the left strip just like the reference. */}
          <Typography
            sx={{
              width: SIDEBAR_WIDTH - 16,
              color: tokens.colors.admin.headerText,
              fontWeight: tokens.font.weights.bold,
              fontSize: tokens.font.sizes.h3,
              letterSpacing: 0.5,
            }}
          >
            Find Geeks
          </Typography>

          {/* Search icon — visual anchor for global search (Phase-2 wires
              the overlay; Phase-1 it's a click target + keyboard shortcut). */}
          <Tooltip title="Search">
            <IconButton
              color="inherit"
              size="medium"
              aria-label="Search"
              sx={{ color: tokens.colors.admin.headerText }}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>

          <Box sx={{ flexGrow: 1 }} />

          {/* Right cluster: Setting + Sign out as ICON + LABEL buttons (white).
              Reference TVP2 §9 image2 shows these as labelled buttons, NOT
              icon-only. */}
          <Button
            color="inherit"
            startIcon={<GearIcon />}
            aria-label="Settings"
            sx={{
              color: tokens.colors.admin.headerText,
              textTransform: "none",
              fontSize: tokens.font.sizes.bodySm,
              fontWeight: tokens.font.weights.regular,
            }}
          >
            Setting
          </Button>
          <Button
            color="inherit"
            startIcon={<PowerIcon />}
            onClick={handleSignOut}
            aria-label="Sign out"
            sx={{
              color: tokens.colors.admin.headerText,
              textTransform: "none",
              fontSize: tokens.font.sizes.bodySm,
              fontWeight: tokens.font.weights.regular,
            }}
          >
            Sign out
          </Button>
        </Toolbar>
      </AppBar>

      {/* Sidebar — white bg w/ subtle right border (reference TVP2 §9 image2) */}
      <Drawer
        variant="permanent"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
            background: tokens.colors.admin.sidebar,
            color: tokens.colors.admin.sidebarText,
            borderRight: `1px solid ${tokens.colors.admin.border}`,
          },
        }}
      >
        {/* Empty Toolbar = vertical spacer pushing nav items below the
            fixed-position AppBar (which carries the "Find Geeks" brand). */}
        <Toolbar variant="dense" sx={{ minHeight: HEADER_HEIGHT }} />
        <Box
          component="nav"
          aria-label="Main navigation"
          sx={{ overflow: "auto", flex: 1 }}
        >
          <List dense disablePadding>
            {navItems.map((item) => {
              if (!can(item.permission)) return null;
              const visibleChildren = (item.children ?? []).filter((c) =>
                can(c.permission),
              );
              if (item.children && visibleChildren.length === 0) return null;

              if (item.children) {
                const open = openGroups[item.label] ?? isGroupActive(item);
                return (
                  <Box key={item.label}>
                    <ListItemButton
                      onClick={() => toggleGroup(item.label)}
                      sx={{
                        color: "inherit",
                        py: 1,
                        borderLeft: "3px solid transparent",
                        "&:hover": {
                          backgroundColor: tokens.colors.admin.sidebarHover,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: "inherit",
                          minWidth: 36,
                          "& svg": { fontSize: 20 },
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: tokens.font.sizes.bodySm,
                          noWrap: true,
                        }}
                      />
                      {open ? (
                        <ExpandLess sx={{ fontSize: 18, color: tokens.colors.admin.textMuted }} />
                      ) : (
                        <ExpandMore sx={{ fontSize: 18, color: tokens.colors.admin.textMuted }} />
                      )}
                    </ListItemButton>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                      <List dense disablePadding>
                        {visibleChildren.map((child) => {
                          const active = isActive(child.path);
                          return (
                            <ListItemButton
                              key={child.path}
                              onClick={() => goToPage(child.path, child.label)}
                              aria-current={active ? "page" : undefined}
                              sx={{
                                pl: 6.5, // align under parent's text (icon=36 + parent_pl=16)
                                py: 0.75,
                                color: active
                                  ? tokens.colors.admin.sidebarActive
                                  : tokens.colors.admin.textMuted,
                                fontWeight: active
                                  ? tokens.font.weights.semibold
                                  : tokens.font.weights.regular,
                                borderLeft: active
                                  ? `3px solid ${tokens.colors.admin.sidebarActive}`
                                  : "3px solid transparent",
                                backgroundColor: active
                                  ? "rgba(0,188,212,0.08)"
                                  : "transparent",
                                "&:hover": {
                                  backgroundColor: tokens.colors.admin.sidebarHover,
                                },
                              }}
                            >
                              <ListItemText
                                primary={child.label}
                                primaryTypographyProps={{
                                  fontSize: tokens.font.sizes.caption,
                                }}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Collapse>
                  </Box>
                );
              }

              const active = isActive(item.path);
              return (
                <ListItemButton
                  key={item.label}
                  onClick={() => item.path && goToPage(item.path, item.label)}
                  aria-current={active ? "page" : undefined}
                  sx={{
                    py: 1,
                    color: active
                      ? tokens.colors.admin.sidebarActive
                      : "inherit",
                    fontWeight: active
                      ? tokens.font.weights.semibold
                      : tokens.font.weights.regular,
                    borderLeft: active
                      ? `3px solid ${tokens.colors.admin.sidebarActive}`
                      : "3px solid transparent",
                    backgroundColor: active
                      ? "rgba(0,188,212,0.08)"
                      : "transparent",
                    "&:hover": {
                      backgroundColor: tokens.colors.admin.sidebarHover,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: "inherit",
                      minWidth: 36,
                      "& svg": { fontSize: 20 },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: tokens.font.sizes.bodySm,
                      noWrap: true,
                    }}
                  />
                </ListItemButton>
              );
            })}
            {/* Divider between nav items and Sign out — reference TVP2 §9
                image2 shows clear visual separation w/ extra vertical space. */}
            <Divider sx={{ my: 1.5 }} />
            <ListItemButton
              onClick={handleSignOut}
              sx={{
                color: "inherit",
                py: 1,
                "&:hover": {
                  backgroundColor: tokens.colors.admin.sidebarHover,
                },
              }}
            >
              <ListItemIcon
                sx={{ color: "inherit", minWidth: 36, "& svg": { fontSize: 20 } }}
              >
                <SignOutIcon />
              </ListItemIcon>
              <ListItemText
                primary="Sign out"
                primaryTypographyProps={{
                  fontSize: tokens.font.sizes.bodySm,
                }}
              />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>

      {/* Main content (TabsBar + page) */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          background: tokens.colors.admin.bg,
          minHeight: "100vh",
          pt: `${HEADER_HEIGHT}px`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TabsBar
          tabs={tabs}
          activePath={location.pathname}
          onTabClick={(path) => navigate(path)}
          onClose={(path) => closeTab(path, location.pathname)}
        />
        <Box sx={{ flex: 1 }}>{children}</Box>
      </Box>

      <Snackbar
        open={Boolean(errorMsg)}
        autoHideDuration={5000}
        onClose={() => setErrorMsg(null)}
        message={errorMsg}
      />
    </Box>
  );
}
